import { useEffect, useState } from 'react';
import { Alert, Checkbox, FormControlLabel, Snackbar } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';
import { useAuthorize } from '../../hooks/use-authorize';
import { UserPermissionName } from '../../models/user-permission-name';

interface CheckboxFieldProps {
  valueKey: string;
  labelKey: string;
  name: string;
  groupKey: string;
  optionData: any[];
  parentKey: string;
  roleCode: string;
}

interface CheckboxesProps {
  valueKey: string;
  labelKey: string;
  groupKey: string;
  parentKey: string;
  optionData: any[];
  onChange: any;
  value: any[];
  roleCode: string;
}

interface CheckboxGroupedData {
  [key: string]: any[];
}

const checkAssignedValues = (
  parentKey: string,
  valueKey: string,
  selectedValue: any,
  checkedValues: any[],
  optionData: any[]
) => {
  const filteredArray: any[] = optionData.filter((item) => checkedValues.includes(item[valueKey]));
  const isPermissionAvailable = filteredArray.some(
    (permission) => permission[parentKey] && permission[parentKey].includes(Number(selectedValue))
  );

  let result;
  if (isPermissionAvailable) result = { value: checkedValues, action: true };
  else result = { value: checkedValues.filter((v: any) => v.toString() !== selectedValue.toString()), action: false };
  return result;
};

const groupBy = (arr: any[], key: string): CheckboxGroupedData => {
  return arr.reduce((acc: any, obj: any) => {
    const keyValue = obj[key];
    if (!acc[keyValue]) {
      acc[keyValue] = [];
    }
    acc[keyValue].push(obj);
    return acc;
  }, {});
};

const FormCheckBoxGroup = ({
  labelKey,
  valueKey,
  groupKey,
  parentKey,
  optionData,
  onChange,
  value,
  roleCode
}: CheckboxesProps) => {
  const [checkedValues, setCheckedValues] = useState<any[]>(value);
  const groupedData = groupBy(optionData, groupKey);
  const [open, setOpen] = useState(false);
  const [isSelectAllChecked, setIsSelectAllChecked] = useState(false);

  useEffect(() => {
    // Check if all options are selected initially
    if (optionData.length > 0 && value?.length === optionData.length) {
      setIsSelectAllChecked(true);
    } else {
      setIsSelectAllChecked(false);
    }
  }, [optionData, value]);

  const handleChange = (e: any) => {
    const {
      target: { checked, value: itemValue },
    } = e;

    if (checked) {
      const viewPermissionID: any[] = optionData?.find((val: any) => val[valueKey] == itemValue)[parentKey];
      const values = Array.from(new Set([...checkedValues, Number(itemValue), ...viewPermissionID]));
      setCheckedValues(values);
      onChange(values);

      // If all checkboxes are selected, mark "Select All" as checked
      if (values?.length === optionData?.length) {
        setIsSelectAllChecked(true);
      }
    } else {
      const response = checkAssignedValues(parentKey, valueKey, itemValue, checkedValues, optionData);
      setOpen(response.action);
      setCheckedValues(response.value);
      onChange(response.value);

      if (response?.value?.length !== optionData?.length) {
        setIsSelectAllChecked(false);
      }
    }
  };

  const handleSelectAllChange = (e: any) => {
    const { checked } = e.target;

    if (checked) {
      // Select all non-system defined checkboxes
      const allValues = optionData.map((item) => item[valueKey]);
      setCheckedValues(allValues);
      setIsSelectAllChecked(true);
      onChange(allValues);
    } else {
      // Unselect all checkboxes
      setCheckedValues([]);
      setIsSelectAllChecked(false);
      onChange([]);
    }
  };

  const isReadOnlyPermission = useAuthorize([UserPermissionName?.READ_ONLY]);

  return (
    <>
      {/* Select All Checkbox */}
      {/* <FormControlLabel
        control={<Checkbox checked={isSelectAllChecked} onChange={handleSelectAllChange} />}
        label="Select All"
      /> */}
      {Object.keys(groupedData).map((key: string) => {
        return (
          key !== "ReadOnly" && (
            <div key={key} className="cardRole">
              <h3 className="createRoleHead">{key}</h3>
              {groupedData[key].map((d: any) => {
                return (
                  <FormControlLabel
                    key={d[valueKey]}
                    control={
                      <Checkbox
                        onChange={handleChange}
                        value={d[valueKey]}
                        disabled={(roleCode === 'ROP' && roleCode !== null) || isReadOnlyPermission}
                        checked={value?.some((v) => v.toString() === d[valueKey].toString())}
                      />
                    }
                    label={d[labelKey]}
                  />
                );
              })}
            </div>
          ));
      })}
      <Snackbar
        open={open}
        autoHideDuration={6000}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        onClose={() => setOpen(false)}
      >
        <Alert severity="error" variant="filled" sx={{ width: '100%' }} onClose={() => setOpen(false)}>
          You are not able to uncheck the view.
        </Alert>
      </Snackbar>
    </>
  );
};

export const CheckboxGroupField = ({
  valueKey,
  labelKey,
  groupKey,
  name,
  optionData,
  parentKey,
  roleCode
}: CheckboxFieldProps): any => {
  const { control } = useFormContext();
  return (
    <div>
      <Controller
        control={control}
        name={name}
        render={({ field: { onChange, value } }) => {
          return (
            <FormCheckBoxGroup
              optionData={optionData}
              onChange={onChange}
              labelKey={labelKey}
              valueKey={valueKey}
              groupKey={groupKey}
              parentKey={parentKey}
              value={value ?? []}
              roleCode={roleCode}
            />
          );
        }}
      />
    </div>
  );
};
